<template>
  <!-- SuperZoom Slide -->
  <div class="uk-width-1-1 uk-cover-container" uk-height-viewport v-show="this.$parent.content_type === 'superzoom'">
    <div id="super_zoom_viewer" uk-cover></div>
    <!-- Canvas for POIs -->
    <canvas
        ref="poiCanvas"
        id="poi_canvas"
        style="position: absolute; top: 0; left: 0; pointer-events: none; z-index: 10;"
    ></canvas>
    <a
        class="uk-position-absolute"
        href="#" uk-marker
        v-on:click="getCoords()"
        v-bind:class="[ 'uk-text-uppercase', 'uk-text-bold', 'uk-transform-left' ]"
        v-bind:style="{ left: 10 + '%', top: 25 + '%' }"
        v-show="development">
      GetCoords
    </a>
  </div>
</template>

<script>
if (typeof nw === "undefined") {
  let nw = window.nw;
}
import OpenSeadragon from 'openseadragon';
import 'gifler';

export default {
  name: "superzoom-slide",
  data: function () {
    return {
      debug: false,
      development: false,    // Enables a dev icon to quickly copy the current camera position to your clipboard
      home: true,
      navigator: false,    // Adds a mini map to the gfxViewer (detail viewer), requires you to enable the navigator boolean on the gfxViewer Config
      viewer: null,
      position: null,
      zoom: null,
      config: [],       // Local config copy for easier path adjustments
      scaledPOIs: new Map(),
      // Canvas-related properties
      poiCanvas: null,
      poiContext: null,
      canvasImageCache: new Map(),
      animationFrameId: null,
      // Add flags to prevent redundant draws
      drawPending: false,
      isInitializing: false,
      // GIF animation properties
      giflers: new Map(), // Store gifler instances for each POI
      giflerLoaded: false
    }
  },
  mounted() {
    if (this.debug) console.log('Mount SuperZoom Slide');

    this.development = nw.global.developmentMode;

    // Load gifler library
    this.loadGifler().then(() => {
      this.giflerLoaded = true;
      // Initialize canvas after gifler is loaded
      this.initCanvas();
    });

    // Apply custom styling to our minimap
    if (this.navigator) {
      // Overwrite the style of the minibar
      var navigator = document.getElementsByClassName('navigator');
      navigator[0].style.background = "#ebe6d3";
      navigator[0].style.border = "2px solid #fff";
      navigator[0].style.borderTop = "none";
      // and its display region
      var displayRegion = document.getElementsByClassName('displayregion');
      displayRegion[0].style.border = "2px solid #d76921";
    }

    // Broadcast that our component is ready to be used
    this.$emit('mountedSlide', 'superzoom-slide');
  },
  beforeDestroy() {
    // Clean up canvas resources
    this.cleanupCanvas();
  },
  methods: {
    //
    // GIF Loading Methods
    //
    loadGifler: function () {
      return new Promise((resolve, reject) => {
        if (window.gifler) {
          resolve();
        }
      });
    },

    initGifAnimation: function (poiId, gifSrc, poiData) {
      if (!window.gifler || !this.poiCanvas) return;

      // Create a temporary canvas for this GIF
      const tempCanvas = document.createElement('canvas');

      let gifDimensions = null;

      const onDrawFrame = (ctx, frame) => {
        // Set canvas size only once based on the first frame or GIF dimensions
        if (!gifDimensions) {
          // Use the logical dimensions of the GIF, not individual frame buffer sizes
          gifDimensions = {
            width: frame.width || frame.buffer.width,
            height: frame.height || frame.buffer.height
          };
          ctx.canvas.width = gifDimensions.width;
          ctx.canvas.height = gifDimensions.height;
        }

        // Clear the entire canvas
        ctx.clearRect(0, 0, gifDimensions.width, gifDimensions.height);

        // Draw the frame at its specified position within the GIF canvas
        ctx.drawImage(frame.buffer, frame.x || 0, frame.y || 0);

        // Trigger a redraw of the main POI canvas
        this.scheduleDraw();
      };

      // Load the GIF and start animation
      gifler(gifSrc).frames(tempCanvas, onDrawFrame);

      // Store the canvas for this POI
      this.giflers.set(poiId, {
        canvas: tempCanvas,
        src: gifSrc
      });
    },


    //
    // Canvas Methods
    //
    initCanvas: function () {
      if (this.poiCanvas) return;

      this.poiCanvas = this.$refs.poiCanvas;
      this.poiContext = this.poiCanvas.getContext('2d');
      // Set up high DPI support
      this.poiCanvas.style.width = '100%';
      this.poiCanvas.style.height = '100%';
      // Update canvas size on window resize
      window.addEventListener('resize', this.resizeCanvas);
    },

    resizeCanvas: function () {
      if (!this.poiCanvas || !this.viewer) return;

      const viewerElement = this.viewer.element;
      const rect = viewerElement.getBoundingClientRect();
      const devicePixelRatio = window.devicePixelRatio || 1;

      // Set actual canvas size in memory (scaled up for high DPI)
      this.poiCanvas.width = rect.width * devicePixelRatio;
      this.poiCanvas.height = rect.height * devicePixelRatio;

      // Scale the canvas back down using CSS
      this.poiCanvas.style.width = rect.width + 'px';
      this.poiCanvas.style.height = rect.height + 'px';

      // Reset transform before scaling
      this.poiContext.setTransform(1, 0, 0, 1, 0, 0);
      // Apply DPI scaling once
      this.poiContext.scale(devicePixelRatio, devicePixelRatio);

      // Only redraw if not during initialization
      if (!this.isInitializing) {
        this.scheduleDraw();
      }
    },

    // New method to schedule drawing and prevent redundant calls
    scheduleDraw: function () {
      if (this.drawPending) return;

      this.drawPending = true;
      this.drawPOIs();
      this.drawPending = false;
    },

    loadPOIImage: function (src) {
      return new Promise((resolve, reject) => {
        if (this.canvasImageCache.has(src)) {
          resolve(this.canvasImageCache.get(src));
          return;
        }

        const img = new Image();
        img.onload = () => {
          this.canvasImageCache.set(src, img);
          resolve(img);
        };
        img.onerror = reject;
        img.src = src;
      });
    },

    drawPOIs: function () {
      if (!this.poiCanvas || !this.poiContext || this.$parent.content_type !== 'superzoom') return;

      // Use a more efficient clear method
      this.poiContext.clearRect(0, 0, this.poiCanvas.width / (window.devicePixelRatio || 1), this.poiCanvas.height / (window.devicePixelRatio || 1));

      if (!this.scaledPOIs?.size) return;

      // Batch viewport calculations to reduce redundant calculations
      const viewport = this.viewer.viewport;
      const viewerBounds = this.viewer.element.getBoundingClientRect();

      for (const [id, poiData] of this.scaledPOIs.entries()) {
        if (!poiData[1].className?.includes('scaled-superzoom-poi')) continue;
        if (poiData[1].classList.contains('uk-hidden')) continue;

        // Pre-calculate visibility to skip invisible POIs early
        const rect = new OpenSeadragon.Rect(poiData[0].x, poiData[0].y, poiData[0].width, poiData[0].height);
        const newRect = viewport.viewportToViewerElementRectangle(
            viewport.imageToViewportRectangle(rect)
        );

        // Skip if not visible (with a small buffer for smooth transitions)
        if (newRect.x + newRect.width < 0 || newRect.y + newRect.height < 0 ||
            newRect.x > viewerBounds.width || newRect.y > viewerBounds.height) {
          continue;
        }

        this.drawPOI(id, poiData[0], poiData[1]);
      }
    },

    drawPOI: async function (id, poiData, element) {
      if (!element) return;
      if (element.classList.contains('uk-hidden')) return;

      const rect = new OpenSeadragon.Rect(poiData.x, poiData.y, poiData.width, poiData.height);
      const newRect = this.viewer.viewport.viewportToViewerElementRectangle(
          this.viewer.viewport.imageToViewportRectangle(rect)
      );

      // Check if POI is visible in current viewport
      if (newRect.x + newRect.width < 0 || newRect.y + newRect.height < 0 ||
          newRect.x > this.poiCanvas.width || newRect.y > this.poiCanvas.height) {
        return; // Skip drawing if not visible
      }

      const style = window.getComputedStyle(element);
      const img = element.querySelector('img');

      if (!img || !img.src) return;

      try {
        // Check if this is a GIF and we have a gifler instance for it
        const giflerData = this.giflers.get(id);
        if (img.src.toLowerCase().endsWith('.gif') && giflerData) {
          // Draw the animated GIF frame
          this.poiContext.save();
          this.poiContext.globalAlpha = parseFloat(style.opacity) || 1;
          this.poiContext.drawImage(giflerData.canvas, newRect.x, newRect.y, newRect.width, newRect.height);
          this.poiContext.restore();
        } else if (img.src.toLowerCase().endsWith('.gif') && this.giflerLoaded) {
          // Initialize GIF animation for this POI
          this.initGifAnimation(id, img.src, poiData);
          // Draw static image for now
          const image = await this.loadPOIImage(img.src);
          this.poiContext.save();
          this.poiContext.globalAlpha = parseFloat(style.opacity) || 1;
          this.poiContext.drawImage(image, newRect.x, newRect.y, newRect.width, newRect.height);
          this.poiContext.restore();
        } else {
          // Draw static image
          const image = await this.loadPOIImage(img.src);
          this.poiContext.save();
          this.poiContext.globalAlpha = parseFloat(style.opacity) || 1;
          this.poiContext.drawImage(image, newRect.x, newRect.y, newRect.width, newRect.height);
          this.poiContext.restore();
        }
      } catch (error) {
        console.warn('Failed to load POI image:', img.src, error);
      }
    },

    cleanupCanvas: function () {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }
      window.removeEventListener('resize', this.resizeCanvas);
      this.canvasImageCache.clear();
      this.giflers.clear();
    },

    //
    // Standard Component Methods
    // Called to enable (activate our module)
    load: function (id) {
      if (this.debug) console.log('Init SuperZoom Slide', this.$parent.config[id].name);

      this.isInitializing = true; // Set initialization flag

      if (this.viewer) {
        this.config = [];
        this.viewer.destroy();
        this.viewer = null;
      }
      // Make a copy of our parents relevant config
      this.config = JSON.parse(JSON.stringify(this.$parent.config[id].content));
      // and apply it to our vue instance
      this.home = this.config.home;
      this.navigator = this.config.navigator;

      // Create a new Viewer instance with the settings of the current slide defined on our parent element
      this.viewer = OpenSeadragon(this.config.settings);
      this.viewer.viewport.panTo(new OpenSeadragon.Point(Number(this.config.position.x), Number(this.config.position.y)), true);
      this.viewer.viewport.zoomTo(Number(this.config.settings.defaultZoomLevel), true);

      // Initial Binding of our Scaled POIs (keep DOM elements hidden for reference)
      this.viewer.addHandler("open", function () {
        // Move our POI Canvas for proper layering & scaling
        if (this.viewer?.canvas && this.poiCanvas) {
          this.viewer.canvas.appendChild(this.poiCanvas);
        }

        const tileSources = this.config?.settings?.tileSources;
        if (!tileSources) return;

        const points = tileSources.scaledOverlays;
        if (!points?.length) return;

        for (let i = 0; i < points.length; i++) {
          if (!points[i]?.className?.includes("scaled-superzoom-poi")) continue;
          const elements = document.querySelectorAll(`#${points[i].id}`);
          if (elements.length === 0) continue;

          const element = elements[0];
          if (!element) continue;

          element.classList = points[i].className;
          // Hide the DOM element since we'll draw it on canvas
          element.style.display = 'none';
          this.scaledPOIs.set(points[i].id, [points[i], element]);

          points[i].originalWidth = element.offsetWidth || 100;
          points[i].originalHeight = element.offsetHeight || 100;
        }

        // Resize canvas to match viewer and draw initial POIs
        this.resizeCanvas();
        this.isInitializing = false; // Clear initialization flag
        this.scheduleDraw(); // Use scheduled draw instead of direct call
      }.bind(this));

      // Use optimized canvas redraw instead of DOM manipulation
      this.viewer.addHandler("update-viewport", this.$parent.debounce(() => {
        this.scheduleDraw();
      }, 8));

      this.viewer.addHandler('animation', () => {
        this.scheduleDraw();
      });

      this.viewer.addHandler('animation-finish', () => {
        this.reachedTarget();
      });

      this.viewer.addHandler("after-resize", () => {
        setTimeout(() => {
          this.resizeCanvas();
          this.scheduleDraw(); // redraw after OS updates viewport
        }, 20);
      });

      this.$parent.content_type = this.$parent.config[id].content.content_type;
      this.$parent.setupScene(id);
    },

    setup: function (id) {
      if (this.debug) console.log('Setup SuperZoom Slide');

      setTimeout(() => {
        if (this.home) {
          this.goHome();
        } else {
          this.viewer.viewport.panTo(new OpenSeadragon.Point(Number(this.config.position.x), Number(this.config.position.y)), true);
          this.viewer.viewport.zoomTo(Number(this.config.settings.defaultZoomLevel), true);
        }
      }, 50);
    },

    reset: function (id) {
      if (this.debug) console.log('Reset SuperZoom Slide');

      if (this.$parent.config[id].content.content_type === 'superzoom') {
        this.goHome();

        // Clear and re-initialize all GIF animations
        this.giflers.clear();
        for (const [poiId, poiData] of this.scaledPOIs.entries()) {
          const element = poiData[1];
          const img = element.querySelector('img');
          if (img && img.src.toLowerCase().endsWith('.gif')) {
            this.initGifAnimation(poiId, img.src, poiData[0]);
          }
        }

        this.showOverlays();
        this.scheduleDraw();
      }
    },

    //
    // Custom Component Methods

    // Reset the camera position
    goHome: function () {
      if (this.debug) console.log('Go Home:', this.viewer);
      this.home = true;
      this.viewer.viewport.goHome();
      this.viewer.setMouseNavEnabled(true);

      if (this.navigator)
        document.getElementsByClassName('navigator')[0].style.display = 'none';

      this.$parent.trackEvent('SuperZoom', 'GoHome', this.$parent.config[this.$parent.currentSlide].display_name);
    },

    // Transition the camera to the given coordinates
    goTo: function (coordinates) {
      if (this.debug) console.log('GoTo:', coordinates);

      const position = coordinates.split(",");
      this.home = false;

      this.viewer.viewport.panTo(new OpenSeadragon.Point(Number(position[1]), Number(position[2])));
      this.viewer.viewport.zoomTo(Number(position[0]));
      this.viewer.setMouseNavEnabled(false);

      if (this.navigator)
        document.getElementsByClassName('navigator')[0].style.display = 'block';

      this.$parent.trackEvent('SuperZoom', 'GoTo', this.$parent.config[this.$parent.currentSlide].display_name, coordinates);
    },

    // Fired, when the camera reached a new position
    reachedTarget: function (event) {
      if (this.development || this.debug) console.log('Reached Target');
    },

    // Legacy method - now handled by canvas drawing
    repositionElements: function () {
      // This method is now replaced by drawPOIs() for canvas-based rendering
      // Keeping it for backward compatibility but redirecting to canvas drawing
      this.scheduleDraw();
    },

    //
    showOverlays: function () {
      if (this.debug) console.log('Show all Overlays');
      const points = document.getElementsByClassName('superzoom-poi');

      for (let i = 0; i < points.length; i++)
        points[i].style.display = 'flex !important';

      // For canvas-based POIs, just redraw them
      this.scheduleDraw();
    },

    showOverlay: function (id) {
      if (this.debug) console.log('Show Overlay:', id);
      // For canvas implementation, we'd need to track visibility state
      // This could be implemented by adding a visible property to POI data
      document.getElementById(id).classList.remove('uk-hidden');
      this.scheduleDraw();
    },

    hideOverlay: function () {
      if (this.debug) console.log('Hide Overlay');
      const points = document.getElementsByClassName('superzoom-poi');

      for (let i = 0; i < points.length; i++)
        points[i].classList.add('uk-hidden');

      if (this.poiContext) {
        this.poiContext.clearRect(0, 0, this.poiCanvas.width, this.poiCanvas.height);
      }
    },

    // Dev Commands for a smoother app setup
    getCoords: function () {
      const coordinates = this.viewer.viewport.getCenter();
      const zoomLevel = this.viewer.viewport.getZoom();

      this.copyToClipboard(zoomLevel.toFixed(3) + ',' + coordinates.x.toFixed(3) + ',' + coordinates.y.toFixed(3));

      if (this.debug) {
        console.log('Get Viewport Coordinates: ', coordinates);
        console.log('Get Zoom: ', zoomLevel);
        console.log('Conversion: ', this.viewer.viewport.viewportToImageCoordinates(coordinates));
      }
    },

    // Helper Function to Copy the current Camera position to the Clipboard of the ,hopefully, developer)
    copyToClipboard(text) {
      const dummy = document.createElement("input");
      document.body.appendChild(dummy);
      dummy.setAttribute('value', text);
      dummy.select();
      document.execCommand("copy");
      document.body.removeChild(dummy);
    }
  }
}
</script>